import * as React from 'react';
import { FC, useState, useEffect } from 'react';
import fetchAPI from 'app/helpers/fetch';
import moment from 'moment/moment';
import { useLocales } from './locales';
import { Popup } from './Popup';
import { LotteryProps, Prize as PrizeT, WheelState, LotteryDates } from './types';
import arrowBack from './images/arrow_back.png';
import arrow from './images/arrow.png';

type PrizeRequestResponse = { round_data: LotteryProps; prize_id: PrizeT['id'] };

type PrizeInfo = {
  name: string;
  description: string;
  image_url: string;
};

const wait = (ms: number) => new Promise((resolve) => setTimeout(() => resolve(true), ms));

type OnWin = (data: LotteryProps) => void;

export const IDLE_ROTATION_TIME = 3000;
export const CONGRATULATION_TIME = 3000;

const useWheel: (
  onWin: OnWin,
  prizes: PrizeT[]
) => [() => Promise<void>, boolean, string | undefined, number, WheelState, number | undefined, () => void] = (
  onWin: OnWin,
  prizes: PrizeT[]
) => {
  const l = useLocales();

  const [error, setError] = useState<string>();
  const [wheelState, setWheelState] = useState<WheelState>('stopped');
  const [arrowAngle, setArrowAngle] = useState<number>(0);
  const [prizeId, setPrizeId] = useState<number>();
  const [stopAngles, setStopAngles] = useState<{ start: number; end: number }>({
    start: 0,
    end: 0,
  });

  const sectorAngle = 360 / prizes.length;

  const playing = wheelState !== 'stopped';

  useEffect(() => {
    if (wheelState !== 'rotation' && wheelState !== 'slowdown') return () => {};

    const i = setInterval(() => {
      setArrowAngle((prevAngle) => {
        let arrowSpeed = 6;
        if (wheelState === 'slowdown' && stopAngles.end > 0 && stopAngles.start > 0)
          arrowSpeed = 6 - (6 / (stopAngles.end - stopAngles.start)) * (prevAngle - stopAngles.start);
        return prevAngle + arrowSpeed;
      });
    }, 1);
    return () => {
      clearInterval(i);
    };
  }, [wheelState, stopAngles]);

  const STOP_OFFSET = 10;

  useEffect(() => {
    if (wheelState !== 'slowdown' || !prizeId) return;

    if (stopAngles.start === 0) {
      const angleOffset = Math.floor(Math.random() * (sectorAngle - 10 - 10 + 1)) + 10;
      const prizeIndex = prizes.findIndex((i) => i.id === prizeId) + 1;
      const prizeAngle = Math.floor(prizeIndex * sectorAngle - angleOffset);
      const finalPosition = arrowAngle - (arrowAngle % 360) + 3 * 360 + prizeAngle + STOP_OFFSET;
      setStopAngles({ start: arrowAngle, end: finalPosition });
    }
  }, [wheelState, arrowAngle, prizeId]);

  useEffect(() => {
    if (wheelState === 'slowdown' && stopAngles.end > 0 && arrowAngle >= stopAngles.end - STOP_OFFSET)
      setWheelState('congratulation');
  }, [wheelState, stopAngles, arrowAngle]);

  const requestPrize = () =>
    fetchAPI('/lottery_prizes_draw', {
      method: 'POST',
      body: null,
    }) as Promise<PrizeRequestResponse>;

  const idleRotation = () => wait(3000);

  const slowdownRotation = () => {
    setWheelState('slowdown');
  };

  const resetWheel = () => {
    setWheelState('stopped');
    setArrowAngle(0);
    setStopAngles({ start: 0, end: 0 });
  };

  const play = async () => {
    if (playing) return;

    setWheelState('rotation');
    await idleRotation();

    let response: PrizeRequestResponse;
    try {
      response = await requestPrize();
    } catch (e) {
      const msg = e ? (e as string) : l('error');
      setError(msg);
      return;
    }

    setPrizeId(response.prize_id);

    slowdownRotation();

    onWin(response.round_data);
  };

  return [play, playing, error, arrowAngle, wheelState, prizeId, resetWheel];
};

const Sector: FC<{
  prize: PrizeT;
  sectorNumber: number;
  sectorAngle: number;
  activeSector: number;
  wheelState: WheelState;
  sectorsLength: number;
}> = ({ prize, sectorNumber, sectorAngle, activeSector, wheelState, sectorsLength }) => {
  const sectorRotation = sectorNumber * sectorAngle - 90 + sectorAngle / 2;
  const sectorStyles = {
    transform: `rotate(${sectorRotation}deg)`,
    '--sectors': sectorsLength,
  };

  const isInUse = ['rotation', 'slowdown', 'congratulation'].includes(wheelState) ? 'ws-i__dark' : '';
  const isWinner = wheelState === 'congratulation' && sectorNumber === activeSector ? 'winner' : '';
  const isActive =
    (wheelState === 'rotation' || wheelState === 'slowdown') && sectorNumber === activeSector ? 'active' : '';
  const sameColors = sectorsLength % 3 === 1 ? 'w-s_item__same-colors' : '';

  return (
    <div
      className={`w-s_item w-s_item__${sectorsLength} ${isActive} ${isWinner} ws-i js-wheel-slice ${isInUse} ${sameColors}`}
      style={sectorStyles}
    >
      <div className="ws-i_wrap">
        <div className="ws-i_text">
          <span>{prize.wheel_text}</span>
        </div>
        <div className="ws-i_image-block">
          <div
            className="ws-i_image"
            style={{
              WebkitMask: `url(${prize.image.file_url}) no-repeat center`,
              mask: `url(${prize.image.file_url}) no-repeat center`,
            }}
            title={prize.wheel_text}
          />
        </div>
      </div>
    </div>
  );
};

const Arrow: FC<{ angle: number }> = ({ angle }) => (
  <div className="wheel_arrow w-arrow">
    <div className="w-arrow_rotatable" style={{ transform: `rotate(${angle}deg)` }}>
      <img src={arrow} alt="arrow" />
    </div>
    <div className="w-arrow_stable">
      <img src={arrowBack} alt="" />
    </div>
  </div>
);

const SectorsList: FC<{
  prizes: PrizeT[];
  arrowAngle: number;
  wheelState: WheelState;
}> = ({ prizes, arrowAngle, wheelState }) => {
  const sectors = prizes.length;
  const sectorAngle = 360 / sectors;
  const activeSector = Math.floor((arrowAngle - Math.floor(arrowAngle / 360) * 360) / sectorAngle);

  return (
    <div className="wheel_wrapper">
      <div className="wheel_sectors w-s">
        <div className="w-s_point">
          <Arrow angle={arrowAngle} />
          {prizes.map((p, i) => (
            <Sector
              prize={p}
              key={p.id}
              sectorNumber={i}
              activeSector={activeSector}
              sectorAngle={sectorAngle}
              wheelState={wheelState}
              sectorsLength={sectors}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const reload = () => {
  window.location.reload();
};

const ErrorPopup: FC<{ text: string }> = ({ text }) => {
  const l = useLocales();
  return (
    <div className="error_popup e-popup fixed-popup" role="alertdialog">
      <div className="e-popup_container fixed-popup_container">
        <div className="e-popup_title">{text}</div>
        <button className="e-popup_button fixed-popup_button" type="button" onClick={reload}>
          {l('reload')}
        </button>
      </div>
    </div>
  );
};

const HowUsePrize: FC<{ prize: PrizeInfo }> = ({ prize }) => {
  const l = useLocales();
  const [instructions, setInstructions] = useState<boolean>(false);

  const changer = () => {
    setInstructions(!instructions);
  };

  if (!instructions || prize.description.length < 15)
    return (
      <div className="wheel_instructions w-instructions">
        <div className="w-instructions_closed">
          <span onClick={changer}>
            {l('use_prize')} {prize.name}
          </span>
        </div>
      </div>
    );

  return (
    <div className="wheel_instructions w-instructions wheel_instructions__opened">
      <div className="w-instructions_opened">
        <div className="td-popup_top td-top">
          <h2 className="td-top_title">
            {l('use_prize')} {prize.name}
          </h2>
          <div className="td-top_close">
            <button type="button" aria-label="close popup" className="td-top_close-button" onClick={changer} />
          </div>
        </div>
        {/* eslint-disable-next-line react/no-danger */}
        <div
          className="td-popup_content"
          aria-label="get ticket info"
          dangerouslySetInnerHTML={{ __html: prize.description }}
        />
      </div>
    </div>
  );
};

const PopupContent: FC<{ prizeInfo: PrizeInfo; resetWheel: () => void }> = ({ prizeInfo, resetWheel }) => {
  const l = useLocales();

  return (
    <>
      <div className="w-popup_info">
        <div className="w-popup_image-wrap">
          <img className="w-popup_image" src={prizeInfo.image_url} alt={prizeInfo.name} />
        </div>
        <span className="w-popup_text">{prizeInfo.name}</span>
      </div>
      <div className="w-popup_buttons">
        <button type="button" className="w-popup_button fixed-popup_button" onClick={resetWheel}>
          {l('continue_playing')}
        </button>
      </div>
      <HowUsePrize prize={prizeInfo} />
    </>
  );
};

const Notification: FC<{ text: string; error?: boolean }> = ({ text, error = false }) => (
  <div className={`wheel_info w-notification${error ? ' w-notification__error' : ''}`} aria-label="wheel notification">
    <div className="w-notification_icon" />
    <div className="w-notification_text">{text}</div>
  </div>
);

const Controls: FC<{
  availableTicketsNumber: number;
  wheelState: WheelState;
  playing: boolean;
  play: () => Promise<void>;
  lotteryDates: LotteryDates;
  lang: string;
}> = ({ availableTicketsNumber, wheelState, playing, play, lotteryDates, lang }) => {
  const l = useLocales();
  moment.locale(lang);

  const today = moment.utc();
  const startDate = moment.utc(lotteryDates.start, 'YYYY-MM-DD');
  const endDate = moment.utc(lotteryDates.end, 'YYYY-MM-DD');

  if (startDate > today) {
    return <Notification text={`${l('lottery_starts')} ${moment(startDate).format('Do MMM')}`} />;
  }
  if (endDate < today) {
    return <Notification text={l('lottery_ends')} />;
  }
  if (!availableTicketsNumber && wheelState === 'stopped') {
    return <Notification text={l('no_tickets')} error />;
  }

  return (
    <div className="wheel_info w-info">
      <div className="w-info_text">
        {l('available_tickets')}: <span className="w-info_text__red">{availableTicketsNumber}</span>
      </div>
      <div className="w-info_buttons">
        <button className="w-info_button" type="button" onClick={play} disabled={playing}>
          <span>{l('win_prize')}</span>
        </button>
      </div>
    </div>
  );
};

const Wheel: FC<{
  availableTicketsNumber: number;
  onWin: OnWin;
  prizes: PrizeT[];
  onStateChange: (callback: WheelState) => void;
  lotteryDates: LotteryDates;
  lang: string;
}> = ({ availableTicketsNumber, onWin, prizes, onStateChange, lotteryDates, lang }) => {
  const l = useLocales();
  const [visiblePopup, setVisiblePopup] = useState<boolean>(false);
  const [play, playing, error, arrowAngle, wheelState, prizeId, resetWheel] = useWheel(onWin, prizes);
  const prizeInfo = {
    name: prizeId ? prizes[prizes.findIndex((i) => i.id === prizeId)]?.name || '' : '',
    description: prizeId ? prizes[prizes.findIndex((i) => i.id === prizeId)]?.description || '' : '',
    image_url: prizeId ? prizes[prizes.findIndex((i) => i.id === prizeId)]?.image.file_url || '' : '',
  };

  useEffect(() => {
    onStateChange(wheelState);
    if (wheelState === 'congratulation') setTimeout(() => setVisiblePopup(true), 1000);
    if (wheelState === 'stopped') setVisiblePopup(false);
  }, [wheelState]);

  if (error) return <ErrorPopup text={error} />;

  return (
    <div className="wheel-block">
      <div className="wheel wheel__spinning">
        <SectorsList prizes={prizes} arrowAngle={arrowAngle} wheelState={wheelState} />
        <Popup
          type="wheel"
          title={l('you_won')}
          visible={visiblePopup}
          render={() => <PopupContent prizeInfo={prizeInfo} resetWheel={resetWheel} />}
          reset={resetWheel}
        />
      </div>
      <Controls {...{ availableTicketsNumber, wheelState, playing, play, lotteryDates, lang }} />
    </div>
  );
};

export default Wheel;
